import { UseSnack } from '@reapit/elements'
import { useCallback, useRef } from 'react'
import { ErrorMessageType, getErrorMessage } from '../error-message'
import { useErrorPagesContext } from '../error-pages-handler'
import { getNetworkInfo } from './getNetworkInfo'
import { refreshSession } from './refreshSession'

interface handleApiQueryProps {
  /**
   * Number of allowed retries before showing the error.
   * Defaults to 3.
   */
  retry?: number
  /**
   * Optional custom error messages based on HTTP status code.
   * sometime we need different message for the error,
   * Example: { 404: 'Not found', 500: 'Server error' }
   */
  customMessage?: Record<number, ErrorMessageType>
}

/**
 * A hook that wraps an async function and only shows an error message after a defined number of retry attempts.
 *
 * Typically used for React Query's `queryFn` to show a user-facing error message only after retries have failed.
 *
 * @param {handleApiQueryProps} [options] - Retry and error message settings.
 * @returns A function that wraps any async function and shows a snack error after `retry` attempts fail.
 *
 * @example
 * const fetchData = async () => await apiClient.get('/endpoint')
 * const queryFn = handleApiQuery({ retry: 2 })(fetchData)
 *
 * useQuery(['data'], queryFn, { retry: 2 })
 */
export const handleApiQuery = (snackToast: UseSnack, { retry = 3, customMessage }: handleApiQueryProps = {}) => {
  const { error: showError } = snackToast
  const { setError } = useErrorPagesContext()
  const attemptRef = useRef(0)

  return useCallback(
    <T>(callBack: () => Promise<T>) =>
      async () => {
        attemptRef.current++
        try {
          return await callBack()
        } catch (err: any) {
          const errorInfo = getNetworkInfo(err)
          const { message, title, isRedirect, isUnautenticated } = getErrorMessage(
            errorInfo.status,
            errorInfo.message,
            errorInfo.title,
            customMessage,
          )

          /**
           * if the error message is @isUnautenticated
           * this will refresh Session, after session refreshed than trow err to do retry once
           */
          if (isUnautenticated && attemptRef.current < retry) {
            await refreshSession() // i'v split the function to make it easy in future development
            // to handle retry max 1 time after session refreshed, so set the limit to max
            attemptRef.current = retry
            throw err
          }

          /**
           * after retry hit the limit we set the error to hook
           */
          if (attemptRef.current > retry) {
            showError(message)
            setError({
              originalErrorMessage: errorInfo.message,
              errorCode: err?.error,
              errorStatus: errorInfo.status,
              errorMessage: message,
              errorTitle: title,
              isRedirect,
            })
          }
          throw err
        }
      },
    [showError],
  )
}
